<template>
  <section class="admin-content" id="agency-settings-page">
    <Navbar />
    <main class="admin-main">

      <!-- Page Header -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center text-white" style="padding: 32px 0; gap: 16px;">
            <div class="header-icon-wrap">
              <i class="fe fe-settings" style="font-size: 22px;"></i>
            </div>
            <div>
              <h4 class="mb-0 font-weight-bold">Agency Settings</h4>
              <small style="opacity: .8;">Manage your agency configuration and preferences</small>
            </div>
          </div>
        </div>
      </div>

      <div class="container mt-n3 pb-5">
        <div class="row">

          <!-- Basic Info -->
          <div class="col-lg-8 col-md-12 mb-4">
            <div class="card table-card shadow">
              <div class="form-card-header">
                <div class="section-number">1</div>
                <div>
                  <p class="mb-0 text-white font-weight-bold" style="font-size:.95rem;">Basic Information</p>
                  <small style="color:rgba(255,255,255,.7);">Agency name, email and contact details</small>
                </div>
              </div>
              <div class="card-body">
                <form @submit.prevent="saveBasicInfo">
                  <div class="form-row">
                    <div class="form-group col-md-6">
                      <label class="info-label d-block mb-1">Agency Name</label>
                      <input type="text" class="form-control" v-model="agency.firstname" placeholder="Enter agency name" />
                    </div>
                    <div class="form-group col-md-6">
                      <label class="info-label d-block mb-1">Email</label>
                      <input type="email" class="form-control" v-model="agency.email" placeholder="Enter email address" />
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="form-group col-md-6">
                      <label class="info-label d-block mb-1">Phone Number</label>
                      <input v-phone type="tel" class="form-control" v-model="agency.phone_number" placeholder="Enter phone number" />
                    </div>
                    <div class="form-group col-md-6">
                      <label class="info-label d-block mb-1">Contact Person Name</label>
                      <input type="text" class="form-control" v-model="agency.lastname" placeholder="Enter contact person name" />
                    </div>
                  </div>
                  <div class="form-group">
                    <label class="info-label d-block mb-1">API Key</label>
                    <div class="input-group">
                      <input :type="showApiKey ? 'text' : 'password'" class="form-control" v-model="agency.api_key"
                        placeholder="API Key" readonly />
                      <div class="input-group-append">
                        <button class="btn btn-outline-secondary" type="button" @click="showApiKey = !showApiKey">
                          <i :class="showApiKey ? 'fe fe-eye-off' : 'fe fe-eye'"></i>
                        </button>
                        <button class="btn btn-outline-secondary" type="button" @click="copyApiKey">
                          <i class="fe fe-copy mr-1"></i> Copy
                        </button>
                      </div>
                    </div>
                    <small class="text-muted">Use this key to authenticate API requests.</small>
                  </div>
                  <button type="submit" class="btn btn-submit px-4" :disabled="savingBasic">
                    <i class="fe fe-save mr-1"></i>
                    {{ savingBasic ? "Saving…" : "Save Basic Info" }}
                  </button>
                </form>
              </div>
            </div>
          </div>

          <!-- Agency ID Card -->
          <div class="col-lg-4 col-md-12 mb-4">
            <div class="card table-card shadow h-100">
              <div class="form-card-header">
                <div class="section-number"><i class="fe fe-info" style="font-size:13px;"></i></div>
                <div>
                  <p class="mb-0 text-white font-weight-bold" style="font-size:.95rem;">Agency ID</p>
                </div>
              </div>
              <div class="card-body">
                <ul class="list-unstyled mb-0">
                  <li class="info-row">
                    <i class="fe fe-hash info-icon"></i>
                    <div>
                      <p class="info-label">Agency ID</p>
                      <p class="info-value"><span class="code-chip">{{ agency.id }}</span></p>
                    </div>
                  </li>
                  <li class="info-row">
                    <i class="fe fe-check-circle info-icon"></i>
                    <div>
                      <p class="info-label">Status</p>
                      <p class="info-value">
                        <span v-if="agency.status" class="badge badge-soft-success">Active</span>
                        <span v-else class="badge badge-soft-warning">Inactive</span>
                      </p>
                    </div>
                  </li>
                  <li class="info-row">
                    <i class="fe fe-calendar info-icon"></i>
                    <div>
                      <p class="info-label">Created</p>
                      <p class="info-value">{{ agency.created_at | moment("YYYY-MM-DD HH:mm") }}</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Advanced Config -->
          <div class="col-lg-12 mb-4">
            <div class="card table-card shadow">
              <div class="form-card-header">
                <div class="section-number">2</div>
                <div>
                  <p class="mb-0 text-white font-weight-bold" style="font-size:.95rem;">Advanced Configuration</p>
                  <small style="color:rgba(255,255,255,.7);">Portal settings, limits, and enrollment rules</small>
                </div>
              </div>
              <div class="card-body">
                <form @submit.prevent="saveAdvancedConfig">
                  <div class="form-row">
                    <div class="form-group col-md-6">
                      <label class="info-label d-block mb-1">Portal URL</label>
                      <input type="url" class="form-control" v-model="agencyConfig.url" placeholder="https://example.com" />
                    </div>
                    <div class="form-group col-md-6">
                      <label class="info-label d-block mb-1">Max Dependents per Enrollee</label>
                      <input type="number" class="form-control" v-model.number="agencyConfig.max_dependents" min="1" max="50" />
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="form-group col-md-6">
                      <label class="info-label d-block mb-1">Max Dependent Child Age</label>
                      <input type="number" class="form-control" v-model.number="agencyConfig.max_dependent_child_age" min="1" max="100" />
                    </div>
                    <div class="form-group col-md-6">
                      <label class="info-label d-block mb-1">Waiting Period (days)</label>
                      <input type="number" class="form-control" v-model.number="agencyConfig.waiting_period" min="0" />
                    </div>
                  </div>

                  <div class="row mb-3">
                    <div class="col-md-4">
                      <div class="custom-control custom-checkbox">
                        <input type="checkbox" class="custom-control-input" id="autoCOF" v-model="agencyConfig.auto_approve_cof" />
                        <label class="custom-control-label" for="autoCOF" style="font-size:.88rem;">
                          Auto-approve Change of Facility
                        </label>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="custom-control custom-checkbox">
                        <input type="checkbox" class="custom-control-input" id="autoDelete" v-model="agencyConfig.auto_delete_enrollees" />
                        <label class="custom-control-label" for="autoDelete" style="font-size:.88rem;">
                          Auto-delete Inactive Enrollees
                        </label>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="custom-control custom-checkbox">
                        <input type="checkbox" class="custom-control-input" id="cofDocRequired" v-model="agencyConfig.cof_document_compulsory" />
                        <label class="custom-control-label" for="cofDocRequired" style="font-size:.88rem;">
                          Require Document for COF
                        </label>
                      </div>
                    </div>
                  </div>

                  <div class="form-group">
                    <label class="info-label d-block mb-1">Welcome SMS to new enrollees</label>
                    <textarea ref="welcomeMessage" class="form-control" rows="4" maxlength="480" v-model="agencyConfig.welcome_message"
                      placeholder="e.g. Welcome {firstname}. Your enrollment with {agency} is received. Your ID number is {id_card_number}."></textarea>
                    <div class="mt-2">
                      <small class="text-muted mr-1">Add:</small>
                      <button v-for="tag in welcomeTags" :key="tag" type="button" class="btn btn-outline-secondary btn-sm mr-1 mb-1"
                        style="padding:0 8px;font-size:.75rem;" @click="addWelcomeTag(tag)">{{ "{" + tag + "}" }}</button>
                    </div>
                    <small class="text-muted d-block">
                      {{ welcomeLength }}/480 characters, about {{ welcomePages }} SMS per enrollee before the details are filled in.
                      Leave empty to send nothing. Sent once, when a principal is enrolled.
                    </small>
                  </div>

                  <div class="form-group">
                    <label class="info-label d-block mb-1">Default HMOs (IDs separated by commas)</label>
                    <textarea class="form-control" rows="2" v-model="defaultHmosText" placeholder="e.g. 59, 29, 60, 27"></textarea>
                  </div>
                  <div class="form-group">
                    <label class="info-label d-block mb-1">Excluded MDAs/Groups (one per line)</label>
                    <textarea class="form-control" rows="4" v-model="excludedMdasText" placeholder="Enter MDA names, one per line"></textarea>
                  </div>

                  <button type="submit" class="btn btn-submit px-4" :disabled="savingAdvanced">
                    <i class="fe fe-save mr-1"></i>
                    {{ savingAdvanced ? "Saving…" : "Save Configuration" }}
                  </button>
                </form>
              </div>
            </div>
          </div>

          <!-- Config Preview -->
          <div class="col-lg-12 mb-4">
            <div class="card table-card shadow">
              <div class="d-flex align-items-center px-4 pt-4 pb-3">
                <i class="fe fe-code text-primary mr-2"></i>
                <h6 class="mb-0 font-weight-bold">Configuration JSON</h6>
              </div>
              <div class="card-body pt-0">
                <pre class="bg-light p-3 rounded" style="font-size:.875rem;max-height:400px;overflow-y:auto;"><code style="color:#333;word-break:break-all;">{{ JSON.stringify(agencyConfig, null, 2) }}</code></pre>
              </div>
            </div>
          </div>

        </div>
      </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
      </div>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

export default {
  components: {
    Navbar,
    Loading,
  },
  data() {
    return {
      agency: {},
      agencyConfig: {
        url: "",
        default_hmos: [],
        excluded_mdas: [],
        primary_color: null,
        max_dependents: 5,
        waiting_period: 60,
        secondary_color: null,
        auto_approve_cof: false,
        auto_delete_enrollees: false,
        cof_document_compulsory: false,
        max_dependent_child_age: 21,
        welcome_message: "",
      },
      welcomeTags: ["firstname", "lastname", "id_card_number", "agency", "facility", "expiry_date"],
      defaultHmosText: "",
      excludedMdasText: "",
      isLoading: false,
      fullPage: true,
      savingBasic: false,
      savingAdvanced: false,
      showApiKey: false,
    };
  },
  computed: {
    welcomeLength() {
      return (this.agencyConfig.welcome_message || "").length;
    },
    welcomePages() {
      return this.welcomeLength <= 160 ? 1 : Math.ceil(this.welcomeLength / 153);
    },
  },
  beforeMount() {
    this.agency = JSON.parse(localStorage.getItem("agency")) || {};
    this.fetchAgencySettings();
  },
  methods: {
    fetchAgencySettings() {
      this.isLoading = true;
      this.axios
        .get(`/api/v1/auth/agency/407465`)
        .then((response) => {
          const data = response.data.data || this.agency;
          this.agency = { ...this.agency, ...data };

          // Parse config if it exists
          if (data.config && typeof data.config === "string") {
            try {
              this.agencyConfig = { ...this.agencyConfig, ...JSON.parse(data.config) };
            } catch (e) {
              console.error(e);
            }
          } else if (data.config && typeof data.config === "object") {
            this.agencyConfig = { ...this.agencyConfig, ...data.config };
          }

          this.updateTextFields();
          this.isLoading = false;
        })
        .catch((error) => {
          console.error(error);
          this.isLoading = false;
          this.$toasted.error("Failed to load agency settings", {
            position: "top-center",
            duration: 3000,
          });
        });
    },
    updateTextFields() {
      this.defaultHmosText = (this.agencyConfig.default_hmos || []).join(", ");
      this.excludedMdasText = (this.agencyConfig.excluded_mdas || []).join("\n");
    },
    parseTextFields() {
      this.agencyConfig.default_hmos = this.defaultHmosText
        .split(",")
        .map((id) => parseInt(id.trim()))
        .filter((id) => !isNaN(id));

      this.agencyConfig.excluded_mdas = this.excludedMdasText
        .split("\n")
        .map((mda) => mda.trim())
        .filter((mda) => mda.length > 0);
    },
    saveBasicInfo() {
      this.savingBasic = true;
      this.axios
        .patch(`/api/v1/auth/agency/407465`, {
          firstname: this.agency.firstname,
          email: this.agency.email,
          phone_number: this.agency.phone_number,
          lastname: this.agency.lastname,
        })
        .then((response) => {
            console.log(response);
          this.$toasted.success("Basic information updated successfully", {
            position: "top-center",
            duration: 3000,
          });
          localStorage.setItem("agency", JSON.stringify(this.agency));
          this.savingBasic = false;
        })
        .catch((error) => {
          console.error(error);
          this.$toasted.error("Failed to save basic information", {
            position: "top-center",
            duration: 3000,
          });
          this.savingBasic = false;
        });
    },
    saveAdvancedConfig() {
      this.parseTextFields();
      this.savingAdvanced = true;

      this.axios
        .patch(`/api/v1/auth/agency/407465`, {
          config: JSON.stringify(this.agencyConfig),
        })
        .then((response) => {
          console.log(response);
          this.$toasted.success("Advanced configuration updated successfully", {
            position: "top-center",
            duration: 3000,
          });
          this.savingAdvanced = false;
        })
        .catch((error) => {
          console.error(error);
          this.$toasted.error("Failed to save advanced configuration", {
            position: "top-center",
            duration: 3000,
          });
          this.savingAdvanced = false;
        });
    },
    addWelcomeTag(tag) {
      const text = this.agencyConfig.welcome_message || "";
      this.agencyConfig.welcome_message = `${text}${text && !text.endsWith(" ") ? " " : ""}{${tag}}`;
      this.$refs.welcomeMessage.focus();
    },
    copyApiKey() {
      if (this.agency.api_key) {
        navigator.clipboard.writeText(this.agency.api_key).then(() => {
          this.$toasted.success("API Key copied to clipboard", {
            position: "top-center",
            duration: 2000,
          });
        });
      }
    },
  },
};
</script>
